export interface OttopayReportRow {
  rowKey: string;
  orderId: string;
  referenceNumber: string;
  merchantId: string;
  terminalId: string;
  paymentMethod: string;
  paymentType: string;
  transactionDate: string;
  storeName: string;
  transactionStatus: string;
  grossAmount: string;
}

export function matchesOttopayFilter(row: any, filter: string): boolean {
  switch (filter) {
    case 'READY_TO_UPDATE':
    case 'ALREADY_COMPLETED':
    case 'UPDATED':
      return row.action === filter;
    case 'UNMATCHED':
    case 'DOUBLE_CHARGE':
      return row.matchStatus === filter;
    case 'NEEDS_REVIEW':
      return ['AMBIGUOUS', 'CONFLICT', 'DOUBLE_CHARGE'].indexOf(row.matchStatus) !== -1 ||
        ['NOT_ELIGIBLE', 'UPDATE_FAILED'].indexOf(row.action) !== -1;
    default:
      return true;
  }
}

const PORTAL_COLUMNS = [
  'Order ID',
  'Reference Number',
  'Payment Method',
  'Payment Type',
  'Transaction Date',
  'Store Name',
  'Status Transaction',
  'Gross Amount'
];

const SETTLEMENT_COLUMNS = [
  'Date',
  'Time',
  'Merchant Name',
  'MID',
  'Settlement Date',
  'Transaction Type',
  'Issuer Ref',
  'TID',
  '#Nominal'
];

const MAX_ROWS = 5000;

export function parseOttopayReport(text: string): OttopayReportRow[] {
  const data = parseCsv(text);
  if (data.length < 2) throw new Error('The OttoPay CSV has no transaction rows.');

  const headers = data[0].map((value, index) =>
    (index === 0 ? value.replace(/^\uFEFF/, '') : value).trim()
  );
  const column = (name: string) => headers.indexOf(name);
  const rows = data.slice(1).filter(values => values.some(value => value.trim() !== ''));
  if (!rows.length) throw new Error('The OttoPay CSV has no transaction rows.');
  if (rows.length > MAX_ROWS) throw new Error(`The OttoPay CSV exceeds ${MAX_ROWS.toLocaleString()} rows.`);

  if (SETTLEMENT_COLUMNS.every(name => column(name) !== -1)) {
    return rows.map((values, index) => {
      const paymentType = valueAt(values, column('Transaction Type')).toUpperCase();
      return {
        rowKey: String(index + 1),
        orderId: '',
        referenceNumber: valueAt(values, column('Issuer Ref')),
        merchantId: valueAt(values, column('MID')),
        terminalId: valueAt(values, column('TID')),
        paymentMethod: settlementPaymentMethod(paymentType),
        paymentType,
        transactionDate: settlementTransactionDate(
          valueAt(values, column('Date')),
          valueAt(values, column('Time'))
        ),
        storeName: valueAt(values, column('Merchant Name')),
        transactionStatus: 'SUCCESS',
        grossAmount: valueAt(values, column('#Nominal'))
      } as OttopayReportRow;
    });
  }

  const missing = PORTAL_COLUMNS.filter(name => column(name) === -1);
  if (missing.length) throw new Error(`Missing CSV column: ${missing.join(', ')}`);

  return rows.map((values, index) => ({
    rowKey: String(index + 1),
    orderId: valueAt(values, column('Order ID')),
    referenceNumber: valueAt(values, column('Reference Number')),
    merchantId: '',
    terminalId: '',
    paymentMethod: valueAt(values, column('Payment Method')),
    paymentType: valueAt(values, column('Payment Type')),
    transactionDate: valueAt(values, column('Transaction Date')),
    storeName: valueAt(values, column('Store Name')),
    transactionStatus: valueAt(values, column('Status Transaction')),
    grossAmount: valueAt(values, column('Gross Amount'))
  } as OttopayReportRow));
}

function settlementTransactionDate(date: string, time: string): string {
  const monthFirst = /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) (\d{1,2}), (\d{4})$/.exec(date);
  const dayFirst = /^(\d{1,2}) (Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec) (\d{4})$/.exec(date);
  if (!monthFirst && !dayFirst) return date;
  const monthName = (monthFirst ? monthFirst[1] : dayFirst![2]).slice(0, 3);
  const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(monthName) + 1;
  const day = monthFirst ? monthFirst[2] : dayFirst![1];
  const year = monthFirst ? monthFirst[3] : dayFirst![3];
  const isoDate = `${year}-${String(month).padStart(2, '0')}-${day.padStart(2, '0')}`;
  return time && time.toLowerCase() !== 'null' ? `${isoDate} ${time}` : isoDate;
}

function settlementPaymentMethod(paymentType: string): string {
  if (paymentType === 'QR' || paymentType === 'QRIS') return 'QRIS';
  if (paymentType === 'DEBIT' || paymentType === 'CREDIT') return 'CARD';
  return paymentType;
}

function valueAt(values: string[], index: number): string {
  return (values[index] || '').trim();
}

function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;

  for (let index = 0; index < text.length; index++) {
    const char = text[index];
    if (quoted) {
      if (char === '"' && text[index + 1] === '"') {
        field += '"';
        index++;
      } else if (char === '"') {
        quoted = false;
      } else {
        field += char;
      }
    } else if (char === '"' && field === '') {
      quoted = true;
    } else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && text[index + 1] === '\n') index++;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += char;
    }
  }
  if (quoted) throw new Error('The OttoPay CSV contains an unfinished quoted value.');
  if (field !== '' || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}
